/**
 * SMS — App 殼（P1-4b：加入導覽 ＋ i18n ＋ ui-kit 樣式）
 *
 * 流程：載入 → 問 `/api/auth/me`
 *   · 401（未登入）→ 顯示登入頁
 *   · 200 → 顯示殼（歡迎語 ＋ 角色 ＋ 導覽 ＋ 登出）
 * ⚠️ 角色**由伺服器決定**，前端唔准「揀角色」（唔可信）；
 *    前端靠角色**隱藏**入口，但**權限一律由後端 guard 把關**（`functions/api/_lib/guard.ts`）。
 * 樣式：一律 `@coeldery/ui-kit` tokens（顏色只喺 `index.css` `:root` 定義）。
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import LocaleSwitcher from '@coeldery/locale-switcher';
import { header, headerInner, h1, page, wrap } from '@coeldery/ui-kit';
import AppRoutes from './components/AppRoutes';
import AppShell from './components/AppShell';
import Login from './pages/Login';
import Setup from './pages/Setup';
import { api, type Me } from './lib/api';
import { SUPPORTED_LANGS, getCurrentLang, setLang, type SupportedLang } from './i18n';

type Phase = 'loading' | 'anon' | 'authed';

/** 掣面顯示（唔用 i18n：語言名本身要「原樣」睇得明） */
const SHORT_LANGS: Record<string, string> = { 'zh-HK': '中', en: 'EN', 'zh-CN': '简' };
const LANGUAGE_NAMES: Record<string, string> = {
  'zh-HK': '繁體中文（香港）',
  en: 'English',
  'zh-CN': '简体中文',
};

export default function App() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const current = getCurrentLang();
  const [phase, setPhase] = useState<Phase>('loading');
  const [user, setUser] = useState<Me | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const res = await api.me();
        if (!alive) return;
        setUser(res.user);
        setPhase('authed');
      } catch {
        if (alive) setPhase('anon');
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const onLoggedIn = useCallback((u: Me) => {
    setUser(u);
    setPhase('authed');
  }, []);

  const onLogout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      /* 就算 API 失敗都照清前端狀態（server 側 cookie 過期會自然失效） */
    }
    setUser(null);
    setPhase('anon');
  }, []);

  const langSwitch = (
    <LocaleSwitcher
      locales={SUPPORTED_LANGS.map((code) => ({ code, label: LANGUAGE_NAMES[code] ?? code }))}
      shortLabels={SHORT_LANGS}
      current={current}
      onChange={(code) => void setLang(code as SupportedLang)}
      ariaLabel={t('app.language')}
    />
  );

  if (phase === 'loading') {
    return (
      <div style={page}>
        <div style={wrap}>{t('home.checking')}</div>
      </div>
    );
  }

  if (phase === 'anon' || !user) {
    return (
      <div style={page}>
        <header style={header}>
          <div style={headerInner}>
            <div>
              <h1 style={h1}>{t('app.title')}</h1>
              <p style={{ margin: 0, fontSize: '14px', color: 'var(--color-text-secondary)' }}>
                {t('app.subtitle')}
              </p>
            </div>
            <div style={{ marginLeft: 'auto' }}>{langSwitch}</div>
          </div>
        </header>
        <div style={wrap}>
          <Login onDone={onLoggedIn} />
        </div>
      </div>
    );
  }

  // ⭐ P1.5：未設定 email ＝ 首次登入 —— **未設定完唔准入 app**（只顯示設定畫面）
  // ⭐ 亦可以由 ⚙ 選單入嚟「改 Email／密碼」（`/setup`；同一個頁面、同一個 API）
  if (user.needsSetup || pathname === '/setup') {
    return (
      <div style={page}>
        <Setup
          user={user}
          onDone={(u) => {
            setUser(u);
            if (!u.needsSetup) navigate('/', { replace: true });
          }}
        />
      </div>
    );
  }

  return (
    <div style={page}>
      <AppShell user={user} onLogout={() => void onLogout()} langSwitch={langSwitch}>
        <AppRoutes user={user} />
      </AppShell>
    </div>
  );
}
